"use client";

import { useState, useMemo, useCallback } from "react";
import {
  Search,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  Check,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import type { TableSelectField, TableColumn, TableRow } from "./types";

// =============================================================================
// TABLE SELECT FIELD RENDERER
// =============================================================================

interface TableSelectFieldRendererProps {
  field: TableSelectField;
  value: string | string[] | undefined;
  onChange: (value: string | string[]) => void;
  error?: string;
  disabled?: boolean;
}

export function TableSelectFieldRenderer({
  field,
  value,
  onChange,
  error,
  disabled,
}: TableSelectFieldRendererProps) {
  // State for search, sort, filter, pagination
  const [searchQuery, setSearchQuery] = useState("");
  const [sortConfig, setSortConfig] = useState<{
    column: string;
    direction: "asc" | "desc";
  } | null>(field.default_sort || null);
  const [activeFilters, setActiveFilters] = useState<Record<string, string>>({});
  const [currentPage, setCurrentPage] = useState(1);

  const pageSize = field.pagination?.page_size || 10;
  const isMultiSelect = field.selection_mode === "multi";

  // Normalize value to array for easier handling
  const selectedIds = useMemo(() => {
    if (!value) return new Set<string>();
    if (Array.isArray(value)) return new Set(value);
    return new Set([value]);
  }, [value]);

  // Filter data based on search and filters
  const filteredData = useMemo(() => {
    let result = [...field.data];

    // Apply search
    if (searchQuery && field.searchable) {
      const query = searchQuery.toLowerCase();
      const searchFields = field.search_fields || field.columns.map((c) => c.key);
      result = result.filter((row) =>
        searchFields.some((key) => {
          const cellValue = row[key];
          return cellValue && String(cellValue).toLowerCase().includes(query);
        })
      );
    }

    // Apply filters
    Object.entries(activeFilters).forEach(([column, filterValue]) => {
      if (filterValue) {
        result = result.filter((row) => String(row[column]) === filterValue);
      }
    });

    return result;
  }, [field.data, field.searchable, field.search_fields, field.columns, searchQuery, activeFilters]);

  // Sort data
  const sortedData = useMemo(() => {
    if (!sortConfig) return filteredData;

    return [...filteredData].sort((a, b) => {
      const aVal = a[sortConfig.column];
      const bVal = b[sortConfig.column];

      if (aVal === bVal) return 0;
      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;

      const comparison =
        typeof aVal === "number" && typeof bVal === "number"
          ? aVal - bVal
          : String(aVal).localeCompare(String(bVal));

      return sortConfig.direction === "asc" ? comparison : -comparison;
    });
  }, [filteredData, sortConfig]);

  // Paginate data
  const paginatedData = useMemo(() => {
    if (!field.pagination?.enabled) return sortedData;
    const start = (currentPage - 1) * pageSize;
    return sortedData.slice(start, start + pageSize);
  }, [sortedData, field.pagination?.enabled, currentPage, pageSize]);

  const totalPages = Math.ceil(sortedData.length / pageSize);

  // Handle row selection
  const handleRowSelect = useCallback(
    (rowId: string) => {
      if (disabled) return;

      if (isMultiSelect) {
        const newSelected = new Set(selectedIds);
        if (newSelected.has(rowId)) {
          newSelected.delete(rowId);
        } else {
          newSelected.add(rowId);
        }
        onChange(Array.from(newSelected));
      } else {
        onChange(rowId);
      }
    },
    [disabled, isMultiSelect, selectedIds, onChange]
  );

  // Handle select all
  const handleSelectAll = useCallback(() => {
    if (disabled || !isMultiSelect) return;

    const allCurrentIds = paginatedData.map((row) => row.id);
    const allSelected = allCurrentIds.every((id) => selectedIds.has(id));

    if (allSelected) {
      // Deselect all on current page
      const newSelected = new Set(selectedIds);
      allCurrentIds.forEach((id) => newSelected.delete(id));
      onChange(Array.from(newSelected));
    } else {
      // Select all on current page
      const newSelected = new Set(selectedIds);
      allCurrentIds.forEach((id) => newSelected.add(id));
      onChange(Array.from(newSelected));
    }
  }, [disabled, isMultiSelect, paginatedData, selectedIds, onChange]);

  // Handle sort
  const handleSort = (column: string) => {
    if (!field.sortable) return;

    const columnDef = field.columns.find((c) => c.key === column);
    if (!columnDef?.sortable) return;

    setSortConfig((prev) => {
      if (prev?.column !== column) {
        return { column, direction: "asc" };
      }
      if (prev.direction === "asc") {
        return { column, direction: "desc" };
      }
      return null;
    });
  };

  // Format cell value
  const formatCellValue = (value: unknown, column: TableColumn): React.ReactNode => {
    if (value === null || value === undefined) return "-";

    switch (column.format) {
      case "number":
        return typeof value === "number" ? value.toLocaleString() : String(value);

      case "date":
        return value instanceof Date
          ? value.toLocaleDateString()
          : String(value);

      case "boolean":
        return value ? (
          <Check className="h-4 w-4 text-emerald-400" />
        ) : (
          <span className="text-muted-foreground">-</span>
        );

      case "badge":
        const badgeColor = column.badge_colors?.[String(value)] || "bg-secondary";
        return (
          <span
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${badgeColor}`}
          >
            {String(value)}
          </span>
        );

      default:
        return String(value);
    }
  };

  // Get sort icon
  const getSortIcon = (column: string) => {
    if (sortConfig?.column !== column) {
      return <ChevronsUpDown className="h-3 w-3 text-muted-foreground" />;
    }
    return sortConfig.direction === "asc" ? (
      <ChevronUp className="h-3 w-3 text-emerald-400" />
    ) : (
      <ChevronDown className="h-3 w-3 text-emerald-400" />
    );
  };

  const allOnPageSelected =
    paginatedData.length > 0 &&
    paginatedData.every((row) => selectedIds.has(row.id));

  return (
    <div className="space-y-2">
      {/* Header with label */}
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-foreground">
          {field.display_name}
          {field.required && <span className="text-red-400 ml-1">*</span>}
        </label>
        {isMultiSelect && selectedIds.size > 0 && (
          <span className="text-xs text-emerald-400">
            {selectedIds.size} selected
          </span>
        )}
      </div>

      {field.description && (
        <p className="text-xs text-muted-foreground">{field.description}</p>
      )}

      {/* Search and Filters */}
      <div className="flex items-center gap-2">
        {field.searchable && (
          <div className="relative flex-1">
            <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              placeholder={field.search_placeholder || "Search..."}
              disabled={disabled}
              className="w-full rounded border border-border bg-muted py-1.5 pl-8 pr-3 text-sm text-foreground placeholder-muted-foreground focus:border-emerald-500 focus:outline-none disabled:opacity-50"
            />
          </div>
        )}

        {/* Column filters */}
        {field.filterable &&
          field.filter_options &&
          Object.entries(field.filter_options).map(([column, options]) => {
            const columnDef = field.columns.find((c) => c.key === column);
            return (
              <select
                key={column}
                value={activeFilters[column] || ""}
                onChange={(e) => {
                  setActiveFilters((prev) => ({
                    ...prev,
                    [column]: e.target.value,
                  }));
                  setCurrentPage(1);
                }}
                disabled={disabled}
                className="rounded border border-border bg-muted px-2 py-1.5 text-sm text-foreground focus:border-emerald-500 focus:outline-none disabled:opacity-50"
              >
                <option value="">All {columnDef?.label || column}</option>
                {options.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            );
          })}
      </div>

      {/* Table */}
      <div
        className="overflow-auto rounded border border-border"
        style={{ maxHeight: field.max_height || "320px" }}
      >
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-muted">
            <tr>
              {/* Selection column */}
              <th className="w-10 border-b border-border p-2">
                {isMultiSelect && (
                  <input
                    type="checkbox"
                    checked={allOnPageSelected}
                    onChange={handleSelectAll}
                    disabled={disabled}
                    className="h-4 w-4 rounded border-border bg-secondary text-emerald-500 focus:ring-emerald-500"
                  />
                )}
              </th>

              {/* Data columns */}
              {field.columns.map((column) => (
                <th
                  key={column.key}
                  className={`border-b border-border p-2 text-left font-medium text-foreground ${
                    column.sortable && field.sortable
                      ? "cursor-pointer hover:bg-secondary/50"
                      : ""
                  }`}
                  style={{ width: column.width }}
                  onClick={() => column.sortable && handleSort(column.key)}
                >
                  <div
                    className={`flex items-center gap-1 ${
                      column.align === "right"
                        ? "justify-end"
                        : column.align === "center"
                        ? "justify-center"
                        : "justify-start"
                    }`}
                  >
                    {column.label}
                    {column.sortable && field.sortable && getSortIcon(column.key)}
                  </div>
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {paginatedData.length === 0 ? (
              <tr>
                <td
                  colSpan={field.columns.length + 1}
                  className="p-8 text-center text-muted-foreground"
                >
                  {field.empty_message || "No data available"}
                </td>
              </tr>
            ) : (
              paginatedData.map((row) => {
                const isSelected = selectedIds.has(row.id);
                return (
                  <tr
                    key={row.id}
                    onClick={() => handleRowSelect(row.id)}
                    className={`cursor-pointer border-b border-border/50 transition-colors ${
                      isSelected
                        ? "bg-emerald-900/20"
                        : "hover:bg-muted/50"
                    } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    {/* Selection cell */}
                    <td className="p-2">
                      {isMultiSelect ? (
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleRowSelect(row.id)}
                          disabled={disabled}
                          onClick={(e) => e.stopPropagation()}
                          className="h-4 w-4 rounded border-border bg-secondary text-emerald-500 focus:ring-emerald-500"
                        />
                      ) : (
                        <div
                          className={`h-4 w-4 rounded-full border-2 ${
                            isSelected
                              ? "border-emerald-500 bg-emerald-500"
                              : "border-border"
                          }`}
                        >
                          {isSelected && (
                            <Check className="h-3 w-3 text-white" />
                          )}
                        </div>
                      )}
                    </td>

                    {/* Data cells */}
                    {field.columns.map((column) => (
                      <td
                        key={column.key}
                        className={`p-2 text-foreground ${
                          column.align === "right"
                            ? "text-right"
                            : column.align === "center"
                            ? "text-center"
                            : "text-left"
                        }`}
                      >
                        {formatCellValue(row[column.key], column)}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {field.pagination?.enabled && totalPages > 1 && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">
            Showing {(currentPage - 1) * pageSize + 1} -{" "}
            {Math.min(currentPage * pageSize, sortedData.length)} of{" "}
            {sortedData.length}
          </span>

          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1 || disabled}
              className="rounded p-1 text-muted-foreground hover:bg-secondary hover:text-foreground disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>

            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum: number;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }

              return (
                <button
                  key={pageNum}
                  type="button"
                  onClick={() => setCurrentPage(pageNum)}
                  disabled={disabled}
                  className={`min-w-[28px] rounded px-2 py-1 ${
                    currentPage === pageNum
                      ? "bg-emerald-600 text-white"
                      : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                  } disabled:opacity-50`}
                >
                  {pageNum}
                </button>
              );
            })}

            <button
              type="button"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages || disabled}
              className="rounded p-1 text-muted-foreground hover:bg-secondary hover:text-foreground disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Quick actions */}
      {isMultiSelect && !disabled && (
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => onChange(field.data.map((r) => r.id))}
            className="text-xs text-muted-foreground hover:text-emerald-400"
          >
            Select All ({field.data.length})
          </button>
          <span className="text-muted-foreground">|</span>
          <button
            type="button"
            onClick={() => onChange([])}
            className="text-xs text-muted-foreground hover:text-red-400"
          >
            Clear Selection
          </button>
        </div>
      )}

      {/* Error */}
      {error && <p className="text-xs text-red-400">{error}</p>}
    </div>
  );
}
